import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Avatar } from "../Auth/AvatarPicker";
import { api } from "../../lib/api";
import "./Leaderboard.css";

// ─── Types ─────────────────────────────────────────────────────────────────────

type LbType = "gold" | "words" | "badges";

interface LeaderboardEntry {
  rank: number;
  userId: string;
  username: string;
  avatarId: string;
  score: number;
  topBadges: string[];
}

// ─── Badge icons ───────────────────────────────────────────────────────────────

const BADGE_ICONS: Record<string, string> = {
  mahtit: "📚", diedalas: "🎓", mahttalas: "⭐",
  duojars: "🔨", meaccar: "🏹",
  matkeolmmaj: "🗺️", mailmmeolmmaj: "🌍",
};

const TAB_CONFIG: { id: LbType; label: string; unit: string; icon: string }[] = [
  { id: "gold",   label: "Gull",   unit: "🪙", icon: "🪙" },
  { id: "words",  label: "Ord",    unit: "ord",  icon: "📖" },
  { id: "badges", label: "Badges", unit: "badges", icon: "🏅" },
];

const RANK_MEDALS = ["🥇", "🥈", "🥉"];

const REFRESH_INTERVAL_MS = 30_000;

// ─── Leaderboard row ───────────────────────────────────────────────────────────

function Row({
  entry,
  isSelf,
  unit,
}: {
  entry: LeaderboardEntry;
  isSelf: boolean;
  unit: string;
}) {
  const rank = entry.rank;
  const rankStr = rank <= 3 ? RANK_MEDALS[rank - 1] : String(rank);
  const rankClass =
    rank === 1 ? "lb-rank--1"
    : rank === 2 ? "lb-rank--2"
    : rank === 3 ? "lb-rank--3"
    : "lb-rank--other";

  const rowClass = [
    "lb-row",
    isSelf ? "lb-row--self" : "",
    rank <= 3 ? `lb-row--rank-${rank}` : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={rowClass} aria-current={isSelf ? "true" : undefined}>
      {/* Rank */}
      <span className={`lb-rank ${rankClass}`}>{rankStr}</span>

      {/* Avatar */}
      <div className="lb-avatar">
        <Avatar avatarId={entry.avatarId} size={36} />
      </div>

      {/* Info */}
      <div className="lb-info">
        <div className="lb-name-row">
          <span className="lb-username">{entry.username}</span>
          {entry.topBadges.length > 0 && (
            <span className="lb-badge-icons">
              {entry.topBadges.map((id) => (
                <span key={id} title={id}>{BADGE_ICONS[id] ?? ""}</span>
              ))}
            </span>
          )}
          {isSelf && <span className="lb-you">deg</span>}
        </div>
      </div>

      {/* Score */}
      <div className="lb-score">
        {entry.score.toLocaleString("nb")}
        <div className="lb-score-unit">{unit}</div>
      </div>
    </div>
  );
}

// ─── LeaderboardScreen ─────────────────────────────────────────────────────────

export function LeaderboardScreen() {
  const navigate = useNavigate();
  const { token, user } = useAuth();

  const [tab, setTab] = useState<LbType>("gold");
  const [entries, setEntries] = useState<Record<LbType, LeaderboardEntry[]>>({
    gold: [], words: [], badges: [],
  });
  const [loading, setLoading] = useState(true);
  const [spinning, setSpinning] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  async function load(showSpin = false) {
    if (showSpin) setSpinning(true);
    const res = await api.get<{ type: string; entries: LeaderboardEntry[] }>(
      `/leaderboard/${tab}`,
      token ?? undefined
    );
    if (showSpin) setSpinning(false);
    if (res.ok) {
      setEntries((prev) => ({ ...prev, [tab]: res.data.entries }));
      setLastUpdated(new Date());
    }
    setLoading(false);
  }

  // Load when tab changes
  useEffect(() => {
    setLoading(true);
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Auto-refresh every 30s
  useEffect(() => {
    intervalRef.current = setInterval(() => { void load(); }, REFRESH_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, token]);

  const currentEntries = entries[tab];
  const activeCfg = TAB_CONFIG.find((t) => t.id === tab)!;

  // Scroll to own row if present
  const selfRank = currentEntries.find((e) => e.userId === user?.userId)?.rank;

  return (
    <div className="lb-page">
      {/* Header */}
      <div className="lb-page__header">
        <button className="lb-page__back" onClick={() => navigate("/map")}>←</button>
        <span className="lb-page__title">Toppliste</span>
        <button
          className={`lb-page__refresh${spinning ? " lb-page__refresh--spinning" : ""}`}
          onClick={() => void load(true)}
          aria-label="Oppdater"
        >
          ↻
        </button>
      </div>

      {/* Tabs */}
      <div className="lb-tabs">
        {TAB_CONFIG.map((t) => (
          <button
            key={t.id}
            className={`lb-tab${tab === t.id ? " lb-tab--active" : ""}`}
            onClick={() => setTab(t.id)}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Body */}
      <div className="lb-body" role="list" aria-label={`Toppliste – ${activeCfg.label}`}>
        {loading ? (
          <p className="lb-empty">Laster…</p>
        ) : currentEntries.length === 0 ? (
          <p className="lb-empty">Ingen oppføringer ennå.</p>
        ) : (
          <>
            {currentEntries.map((entry) => (
              <Row
                key={entry.userId}
                entry={entry}
                isSelf={entry.userId === user?.userId}
                unit={activeCfg.unit}
              />
            ))}
            <p className="lb-footer">
              {selfRank
                ? `Din plassering: #${selfRank} · `
                : "Du er ikke med ennå · "}
              {lastUpdated
                ? `Oppdatert ${lastUpdated.toLocaleTimeString("nb", { hour: "2-digit", minute: "2-digit" })}`
                : ""}
              {" · Oppdateres hvert 30 s"}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
